import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { 
  Truck, MapPin, Sun, Moon, Move, ZoomIn, ZoomOut, RotateCcw, 
  Save, Info, Calendar, Utensils, Phone, User, Beer, Sunrise, Sunset, Coffee
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map image URL - replace with actual uploaded image
const MAP_IMAGE_URL = "https://customer-assets.emergentagent.com/job_fleet-manager-378/artifacts/y1ria6vs_WhatsApp%20Image%202026-03-12%20at%2013.29.47.jpeg";

// Zone colors for markers
const ZONE_MARKER_COLORS = {
  "Volley": { bg: "#FEF08A", border: "#FACC15", text: "#854D0E" },
  "Central": { bg: "#A7F3D0", border: "#10B981", text: "#065F46" },
  "Foot": { bg: "#BFDBFE", border: "#3B82F6", text: "#1E40AF" },
  "Hall d'honneur": { bg: "#FED7AA", border: "#F97316", text: "#9A3412" },
  "BAT S": { bg: "#E9D5FF", border: "#A855F7", text: "#6B21A8" },
};

// Predefined zone positions on the map (percentages) - Ajustées selon la carte de l'utilisateur
const ZONE_POSITIONS = {
  "Foot": { x: 32, y: 18 },           // Terrains de foot - haut gauche
  "Volley": { x: 72, y: 45 },         // Terrains de volley - droite milieu
  "Central": { x: 55, y: 60 },        // Zone centrale - centre-bas (remonté)
  "Hall d'honneur": { x: 48, y: 78 }, // Hall d'honneur - bas centre
  "BAT S": { x: 20, y: 35 },          // BAT S - gauche
  "Bars": { x: 65, y: 55 },           // Bars - centre-droite
};

export default function HECInteractiveMap({ planning, selectedDay, onReload }) {
  const mapRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [positions, setPositions] = useState({});
  const [dragging, setDragging] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [viewService, setViewService] = useState("midi"); // midi or soir

  // Load saved positions from planning
  useEffect(() => {
    if (planning?.map_positions) {
      setPositions(planning.map_positions);
    } else {
      // Initialize with default zone-based positions
      initializePositions();
    }
  }, [planning, selectedDay, viewService]);

  const initializePositions = () => {
    const slots = getSlots();
    const newPositions = {};
    const zoneCounters = {};

    slots.forEach((slot, index) => {
      const zone = slot.zone || "Central";
      const zonePos = ZONE_POSITIONS[zone] || { x: 50, y: 50 };
      
      // Offset each food truck in the same zone
      if (!zoneCounters[zone]) zoneCounters[zone] = 0;
      const count = zoneCounters[zone];
      zoneCounters[zone]++;

      const key = `${selectedDay}-${viewService}-${index}`;
      if (!positions[key]) {
        // Disposition en cercle autour du point central de la zone
        const angle = (count * 45) * (Math.PI / 180); // 45 degrés entre chaque FT
        const radius = count === 0 ? 0 : 6 + Math.floor(count / 8) * 4; // Premier au centre, puis en cercle
        
        newPositions[key] = {
          x: Math.min(92, Math.max(8, zonePos.x + radius * Math.cos(angle))),
          y: Math.min(92, Math.max(8, zonePos.y + radius * Math.sin(angle))),
        };
      }
    });

    setPositions(prev => ({ ...prev, ...newPositions }));
  };

  const getSlots = () => {
    if (!planning?.planning_days || !selectedDay) return [];
    const day = planning.planning_days.find(d => d.date === selectedDay);
    if (!day) return [];
    
    switch (viewService) {
      case "matin":
        return day.matin || [];
      case "midi":
        return day.midi || [];
      case "apres_midi":
        return day.apres_midi || [];
      case "soir":
        return day.soir || [];
      case "nuit":
        return day.nuit || [];
      default:
        return day.midi || [];
    }
  };

  const getBars = () => {
    if (!planning?.planning_days || !selectedDay) return [];
    const day = planning.planning_days.find(d => d.date === selectedDay);
    if (!day) return [];
    return day.bars_soir || [];
  };

  const handleMouseDown = (e, key, slot) => {
    if (!editMode) {
      // Show detail dialog on click when not editing
      setSelectedMarker({ key, slot });
      setShowDetailDialog(true);
      return;
    }
    e.preventDefault();
    setDragging({ key, startX: e.clientX, startY: e.clientY, startPos: positions[key] });
  };

  const handleMouseMove = (e) => {
    if (!dragging || !mapRef.current) return;
    
    const rect = mapRef.current.getBoundingClientRect();
    const deltaX = (e.clientX - dragging.startX) / rect.width * 100;
    const deltaY = (e.clientY - dragging.startY) / rect.height * 100;

    const newX = Math.min(95, Math.max(5, (dragging.startPos?.x || 50) + deltaX));
    const newY = Math.min(95, Math.max(5, (dragging.startPos?.y || 50) + deltaY));

    setPositions(prev => ({
      ...prev,
      [dragging.key]: { x: newX, y: newY }
    }));
  };

  const handleMouseUp = () => {
    setDragging(null);
  };

  const handleTouchStart = (e, key, slot) => {
    if (!editMode) {
      setSelectedMarker({ key, slot });
      setShowDetailDialog(true);
      return;
    }
    const touch = e.touches[0];
    setDragging({ key, startX: touch.clientX, startY: touch.clientY, startPos: positions[key] });
  };

  const handleTouchMove = (e) => {
    if (!dragging || !mapRef.current) return;
    const touch = e.touches[0];
    
    const rect = mapRef.current.getBoundingClientRect();
    const deltaX = (touch.clientX - dragging.startX) / rect.width * 100;
    const deltaY = (touch.clientY - dragging.startY) / rect.height * 100;

    const newX = Math.min(95, Math.max(5, (dragging.startPos?.x || 50) + deltaX));
    const newY = Math.min(95, Math.max(5, (dragging.startPos?.y || 50) + deltaY));

    setPositions(prev => ({
      ...prev,
      [dragging.key]: { x: newX, y: newY }
    }));
  };

  const savePositions = async () => {
    try {
      await axios.put(`${API_URL}/hec/plannings/${planning.id}/map-positions`, positions);
      toast.success("Positions sauvegardées");
      if (onReload) onReload();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const resetPositions = () => {
    setPositions({});
    initializePositions();
  };

  const getZoneColor = (zone) => ZONE_MARKER_COLORS[zone] || ZONE_MARKER_COLORS["Central"];
  
  const getFoodTruckInfo = (foodTruckIdOrNom) => {
    if (!foodTruckIdOrNom) return null;
    // Search by id or nom (case-insensitive)
    const searchTerm = foodTruckIdOrNom.toLowerCase();
    return planning?.food_trucks?.find(ft => 
      ft.id === foodTruckIdOrNom || 
      ft.id?.toLowerCase() === searchTerm ||
      ft.nom?.toLowerCase() === searchTerm
    ) || null;
  };
  
  // Helper to get food truck info from slot (tries id first, then nom)
  const getFoodTruckFromSlot = (slot) => {
    return getFoodTruckInfo(slot?.food_truck_id) || getFoodTruckInfo(slot?.food_truck_nom);
  };

  const slots = getSlots();
  const bars = getBars();

  return (
    <div className="space-y-4">
      {/* Controls - Mobile responsive */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={viewService} onValueChange={setViewService}>
            <SelectTrigger className="w-28 sm:w-32 text-xs sm:text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="matin">
                <span className="flex items-center gap-2">
                  <Sunrise className="w-4 h-4 text-orange-400" />
                  Matin
                </span>
              </SelectItem>
              <SelectItem value="midi">
                <span className="flex items-center gap-2">
                  <Sun className="w-4 h-4 text-yellow-500" />
                  Midi
                </span>
              </SelectItem>
              <SelectItem value="apres_midi">
                <span className="flex items-center gap-2">
                  <Sunset className="w-4 h-4 text-orange-500" />
                  Après-midi
                </span>
              </SelectItem>
              <SelectItem value="soir">
                <span className="flex items-center gap-2">
                  <Moon className="w-4 h-4 text-indigo-500" />
                  Soir
                </span>
              </SelectItem>
              <SelectItem value="nuit">
                <span className="flex items-center gap-2">
                  <Moon className="w-4 h-4 text-slate-600" />
                  Nuit
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex items-center gap-1 border rounded-lg p-1">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}>
              <ZoomOut className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
            <span className="text-xs sm:text-sm px-1 sm:px-2 min-w-[2.5rem] sm:min-w-[3rem] text-center">{Math.round(zoom * 100)}%</span>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setZoom(z => Math.min(2, z + 0.25))}>
              <ZoomIn className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={editMode ? "default" : "outline"}
            size="sm"
            onClick={() => setEditMode(!editMode)}
            className={`text-xs sm:text-sm ${editMode ? "bg-primary" : ""}`}
          >
            <Move className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">{editMode ? "Mode édition ON" : "Éditer positions"}</span>
            <span className="sm:hidden">{editMode ? "ON" : "Éditer"}</span>
          </Button>
          
          {editMode && (
            <>
              <Button variant="outline" size="sm" onClick={resetPositions} className="text-xs sm:text-sm">
                <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Réinitialiser</span>
              </Button>
              <Button size="sm" onClick={savePositions} className="bg-primary hover:bg-primary-hover text-xs sm:text-sm">
                <Save className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Sauvegarder</span>
                <span className="sm:hidden">OK</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Legend - Mobile responsive */}
      <div className="flex flex-wrap gap-2 sm:gap-3">
        {Object.entries(ZONE_MARKER_COLORS).map(([zone, colors]) => (
          <div key={zone} className="flex items-center gap-1 sm:gap-2">
            <div 
              className="w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2" 
              style={{ backgroundColor: colors.bg, borderColor: colors.border }}
            />
            <span className="text-xs sm:text-sm">{zone}</span>
          </div>
        ))}
        <div className="flex items-center gap-1 sm:gap-2 ml-2 sm:ml-4">
          <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-amber-400 border-2 border-amber-600 flex items-center justify-center">
            <Beer className="w-1.5 h-1.5 sm:w-2 sm:h-2 text-amber-800" />
          </div>
          <span className="text-xs sm:text-sm">Bars</span>
        </div>
      </div>

      {/* Map Container with Side Legend */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Side Legend with Specialties - Hidden on mobile, shown on larger screens */}
        <Card className="border-[#E5E0DC] w-full lg:w-64 lg:flex-shrink-0 max-h-[40vh] lg:max-h-[70vh] overflow-y-auto order-2 lg:order-1">
          <CardHeader className="py-2 lg:py-3 border-b border-[#E5E0DC] sticky top-0 bg-white z-10">
            <CardTitle className="text-xs lg:text-sm flex items-center gap-2">
              <Utensils className="w-3 h-3 lg:w-4 lg:h-4 text-primary" />
              Food Trucks présents
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 space-y-1">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-1">
            {slots.map((slot, index) => {
              const colors = getZoneColor(slot.zone);
              const ftInfo = getFoodTruckFromSlot(slot);
              return (
                <div
                  key={index}
                  className="p-2 rounded-lg border cursor-pointer hover:shadow-md transition-all flex items-center gap-2"
                  style={{ 
                    backgroundColor: colors.bg + '40',
                    borderColor: colors.border,
                  }}
                  onClick={() => {
                    setSelectedMarker({ key: `${selectedDay}-${viewService}-${index}`, slot });
                    setShowDetailDialog(true);
                  }}
                >
                  {/* Logo */}
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{ backgroundColor: colors.bg, border: `2px solid ${colors.border}` }}
                  >
                    {ftInfo?.logo_url ? (
                      <img src={ftInfo.logo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Truck className="w-4 h-4" style={{ color: colors.text }} />
                    )}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <p className="text-xs font-semibold truncate" style={{ color: colors.text }}>{slot.specialite}</p>
                      {ftInfo?.propose_cafe && (
                        <Coffee className="w-3 h-3 text-amber-700 flex-shrink-0" title="Propose du café" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{slot.food_truck_nom}</p>
                  </div>
                  {/* Arrow indicator */}
                  <svg className="w-4 h-4 flex-shrink-0" style={{ color: colors.border }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </div>
              );
            })}
            </div>
            
            {/* Bars section */}
            {viewService === "soir" && bars.length > 0 && (
              <>
                <div className="pt-2 mt-2 border-t border-[#E5E0DC]">
                  <p className="text-xs font-semibold text-amber-700 flex items-center gap-1 mb-1">
                    <Beer className="w-3 h-3" />
                    Bars
                  </p>
                </div>
                {bars.map((bar, index) => (
                  <div
                    key={`bar-${index}`}
                    className="p-2 rounded-lg border border-amber-200 bg-amber-50 cursor-pointer hover:shadow-md transition-all flex items-center gap-2"
                    onClick={() => {
                      setSelectedMarker({ key: `${selectedDay}-bar-${index}`, slot: { ...bar, isBar: true } });
                      setShowDetailDialog(true);
                    }}
                  >
                    <div className="w-8 h-8 rounded-full bg-amber-400 border-2 border-amber-600 flex items-center justify-center flex-shrink-0">
                      <Beer className="w-4 h-4 text-amber-800" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-amber-800 truncate">{bar.bar_nom}</p>
                      <p className="text-xs text-amber-600 truncate">{bar.zone}</p>
                    </div>
                  </div>
                ))}
              </>
            )}
          </CardContent>
        </Card>

        {/* Map */}
        <Card className="border-[#E5E0DC] overflow-hidden flex-1 order-1 lg:order-2">
          <CardContent className="p-0">
            <div 
              className="relative overflow-auto bg-gray-100 touch-pan-x touch-pan-y"
              style={{ height: '55vh', minHeight: '300px' }}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleMouseUp}
            >
              <div
                ref={mapRef}
                className="relative inline-block"
                style={{ 
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top left',
                  minWidth: 'max-content'
                }}
              >
                {/* Map Image */}
                <img 
                  src={MAP_IMAGE_URL} 
                  alt="Carte HEC"
                  className="h-auto select-none"
                  style={{ minWidth: '600px', maxWidth: '100vw' }}
                  draggable={false}
                />

                {/* Zone Labels */}
                {Object.entries(ZONE_POSITIONS).map(([zone, pos]) => (
                  <div
                    key={zone}
                    className="absolute transform -translate-x-1/2 pointer-events-none"
                    style={{ left: `${pos.x}%`, top: `${pos.y - 8}%` }}
                  >
                    <Badge 
                      className="text-xs shadow-lg"
                      style={{ 
                        backgroundColor: ZONE_MARKER_COLORS[zone]?.bg,
                        color: ZONE_MARKER_COLORS[zone]?.text,
                        borderColor: ZONE_MARKER_COLORS[zone]?.border,
                      }}
                    >
                      <MapPin className="w-3 h-3 mr-1" />
                      {zone}
                    </Badge>
                  </div>
                ))}

                {/* Food Truck Markers - Compact with number */}
                {slots.map((slot, index) => {
                  const key = `${selectedDay}-${viewService}-${index}`;
                  const pos = positions[key] || { x: 50, y: 50 };
                  const colors = getZoneColor(slot.zone);
                  const ftInfo = getFoodTruckFromSlot(slot);
                  
                  return (
                    <div
                      key={key}
                      className={`absolute transform -translate-x-1/2 -translate-y-1/2 ${editMode ? 'cursor-move' : 'cursor-pointer'} transition-shadow hover:z-50 group`}
                      style={{ 
                        left: `${pos.x}%`, 
                        top: `${pos.y}%`,
                        zIndex: dragging?.key === key ? 100 : 10,
                      }}
                      onMouseDown={(e) => handleMouseDown(e, key, slot)}
                      onTouchStart={(e) => handleTouchStart(e, key, slot)}
                    >
                      {/* Logo or icon */}
                      <div 
                        className={`
                          w-10 h-10 rounded-full border-3 shadow-lg flex items-center justify-center overflow-hidden
                          transition-transform group-hover:scale-125
                          ${editMode ? 'animate-pulse' : ''}
                        `}
                        style={{ 
                          backgroundColor: ftInfo?.logo_url ? 'white' : colors.bg, 
                          borderColor: colors.border,
                          borderWidth: '3px',
                        }}
                      >
                        {ftInfo?.logo_url ? (
                          <img src={ftInfo.logo_url} alt={slot.food_truck_nom} className="w-full h-full object-cover" />
                        ) : (
                          <Truck className="w-5 h-5" style={{ color: colors.text }} />
                        )}
                      </div>
                      
                      {/* Coffee badge */}
                      {ftInfo?.propose_cafe && (
                        <div 
                          className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 border-2 border-white shadow flex items-center justify-center"
                          title="Propose du café"
                        >
                          <Coffee className="w-3 h-3 text-white" />
                        </div>
                      )}
                      
                      {/* Hover tooltip with specialty */}
                      <div 
                        className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap"
                      >
                        <div 
                          className="px-2 py-1 rounded shadow-lg text-xs font-medium"
                          style={{ backgroundColor: colors.border, color: 'white' }}
                        >
                          {slot.specialite}
                          <div 
                            className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0"
                            style={{
                              borderLeft: '6px solid transparent',
                              borderRight: '6px solid transparent',
                              borderTop: `6px solid ${colors.border}`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Bar Markers (soir only) */}
              {viewService === "soir" && bars.map((bar, index) => {
                const key = `${selectedDay}-bar-${index}`;
                const pos = positions[key] || { x: 55 + index * 5, y: 92 };
                
                return (
                  <div
                    key={key}
                    className={`absolute transform -translate-x-1/2 -translate-y-1/2 ${editMode ? 'cursor-move' : 'cursor-pointer'}`}
                    style={{ 
                      left: `${pos.x}%`, 
                      top: `${pos.y}%`,
                      zIndex: dragging?.key === key ? 100 : 10,
                    }}
                    onMouseDown={(e) => handleMouseDown(e, key, { ...bar, isBar: true })}
                    onTouchStart={(e) => handleTouchStart(e, key, { ...bar, isBar: true })}
                  >
                    <div 
                      className={`
                        w-8 h-8 rounded-full bg-amber-400 border-2 border-amber-600 shadow-lg 
                        flex items-center justify-center hover:scale-110 transition-transform
                        ${editMode ? 'animate-pulse' : ''}
                      `}
                    >
                      <Beer className="w-4 h-4 text-amber-800" />
                    </div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 whitespace-nowrap">
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded shadow-sm bg-white text-amber-800 border border-amber-300">
                        {bar.bar_nom?.split(' ').slice(0, 2).join(' ')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 text-center">
          <p className="text-2xl font-bold text-yellow-700">
            {slots.filter(s => s.zone === "Volley").length}
          </p>
          <p className="text-xs text-yellow-600">Zone Volley</p>
        </div>
        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
          <p className="text-2xl font-bold text-emerald-700">
            {slots.filter(s => s.zone === "Central").length}
          </p>
          <p className="text-xs text-emerald-600">Zone Central</p>
        </div>
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
          <p className="text-2xl font-bold text-blue-700">
            {slots.filter(s => s.zone === "Foot").length}
          </p>
          <p className="text-xs text-blue-600">Zone Foot</p>
        </div>
        <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 text-center">
          <p className="text-2xl font-bold text-orange-700">
            {slots.filter(s => s.zone === "Hall d'honneur").length}
          </p>
          <p className="text-xs text-orange-600">Hall d'honneur</p>
        </div>
        <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
          <p className="text-2xl font-bold text-purple-700">
            {slots.filter(s => s.zone === "BAT S").length}
          </p>
          <p className="text-xs text-purple-600">BAT S</p>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedMarker?.slot?.isBar ? (
                <><Beer className="w-6 h-6 text-amber-600" /> {selectedMarker?.slot?.bar_nom}</>
              ) : (
                <>
                  {(() => {
                    const ftInfo = getFoodTruckFromSlot(selectedMarker?.slot);
                    return ftInfo?.logo_url ? (
                      <img src={ftInfo.logo_url} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-primary" />
                    ) : (
                      <Truck className="w-6 h-6 text-primary" />
                    );
                  })()}
                  <span>{selectedMarker?.slot?.food_truck_nom}</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedMarker?.slot && !selectedMarker.slot.isBar && (
            <div className="space-y-4">
              {/* Spécialité */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge 
                  className="text-base px-3 py-1"
                  style={{ 
                    backgroundColor: getZoneColor(selectedMarker.slot.zone).bg,
                    color: getZoneColor(selectedMarker.slot.zone).text,
                  }}
                >
                  <Utensils className="w-4 h-4 mr-2" />
                  {selectedMarker.slot.specialite}
                </Badge>
                <Badge 
                  variant="outline"
                  style={{ 
                    borderColor: getZoneColor(selectedMarker.slot.zone).border,
                    color: getZoneColor(selectedMarker.slot.zone).text,
                  }}
                >
                  <MapPin className="w-3 h-3 mr-1" />
                  {selectedMarker.slot.zone}
                </Badge>
                {(() => {
                  const ftInfo = getFoodTruckFromSlot(selectedMarker.slot);
                  if (ftInfo?.propose_cafe) {
                    return (
                      <Badge className="bg-amber-500 text-white hover:bg-amber-600">
                        <Coffee className="w-3 h-3 mr-1" />
                        Café
                      </Badge>
                    );
                  }
                  return null;
                })()}
              </div>
              
              {/* Menu du jour avec plats, photos, prix et allergènes */}
              {(() => {
                const ftInfo = getFoodTruckFromSlot(selectedMarker.slot);
                
                // Déterminer le jour du menu en fonction de la date et du service
                const getMenuJour = () => {
                  if (!selectedDay) return null;
                  const date = new Date(selectedDay);
                  const dayOfWeek = date.getDay(); // 0=Dim, 1=Lun, ..., 4=Jeu, 5=Ven, 6=Sam
                  
                  if (dayOfWeek === 4) return "Jeudi";
                  if (dayOfWeek === 5) return "Vendredi";
                  if (dayOfWeek === 6) {
                    // Samedi : Midi ou Soir selon le service
                    return viewService === "soir" ? "Samedi Soir" : "Samedi Midi";
                  }
                  return null;
                };
                
                const menuJour = getMenuJour();
                const menuItems = menuJour && ftInfo?.menu_par_jour?.[menuJour]?.length > 0 
                  ? ftInfo.menu_par_jour[menuJour] 
                  : ftInfo?.menu_items || [];
                
                if (menuItems.length > 0) {
                  return (
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-secondary flex items-center gap-2">
                        <Utensils className="w-4 h-4 text-primary" />
                        Menu {menuJour || "du jour"}
                      </p>
                      {menuItems.map((item, i) => (
                        <div key={i} className="p-3 bg-[#F9F7F5] rounded-lg border border-[#E5E0DC]">
                          <div className="flex gap-3">
                            {item.photo_url ? (
                              <img src={item.photo_url} alt={item.nom} className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <Utensils className="w-8 h-8 text-gray-300" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <p className="font-semibold text-secondary">{item.nom}</p>
                                {/* Pas de prix pour Samedi Soir */}
                                {menuJour !== "Samedi Soir" && (
                                  <p className="text-lg font-bold text-primary ml-2">{item.prix?.toFixed(2) || "0.00"}€</p>
                                )}
                              </div>
                              {/* Ingrédients */}
                              {item.ingredients && (
                                <p className="text-sm text-gray-600 mt-1">
                                  <span className="text-muted-foreground">Ingrédients : </span>
                                  {item.ingredients}
                                </p>
                              )}
                              {/* Description si pas d'ingrédients */}
                              {!item.ingredients && item.description && (
                                <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                              )}
                              {/* Allergènes */}
                              {item.allergenes && (
                                <div className="mt-2 flex items-center gap-1 flex-wrap">
                                  <span className="text-xs text-orange-600 font-medium">⚠️ Allergènes:</span>
                                  <span className="text-xs text-orange-600">{item.allergenes}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }
                return (
                  <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 text-center">
                    <Utensils className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-muted-foreground">Menu non renseigné pour {menuJour || "ce jour"}</p>
                  </div>
                );
              })()}

              {/* Photos du food truck */}
              {(() => {
                const ftInfo = getFoodTruckFromSlot(selectedMarker.slot);
                if (ftInfo?.photos && ftInfo.photos.length > 0) {
                  return (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-secondary">Photos</p>
                      <div className="grid grid-cols-3 gap-2">
                        {ftInfo.photos.map((photo, i) => (
                          <img key={i} src={photo} alt="" className="w-full h-20 rounded-lg object-cover" />
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}

          {selectedMarker?.slot?.isBar && (
            <div className="space-y-4">
              <Badge className="bg-amber-100 text-amber-700">
                <MapPin className="w-3 h-3 mr-1" />
                {selectedMarker.slot.zone}
              </Badge>
              
              {/* Carte des boissons du bar */}
              {(() => {
                // Find bar info by bar_id or bar_index
                let barInfo = null;
                if (selectedMarker.slot.bar_id) {
                  barInfo = planning?.bars?.find(b => b.id === selectedMarker.slot.bar_id);
                } else if (selectedMarker.slot.bar_index !== undefined && planning?.bars) {
                  barInfo = planning.bars[selectedMarker.slot.bar_index];
                }
                
                if (barInfo?.boissons && barInfo.boissons.length > 0) {
                  return (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-amber-700 flex items-center gap-2">
                        <Beer className="w-4 h-4" />
                        Carte des boissons
                      </p>
                      <div className="space-y-2">
                        {barInfo.boissons.map((b, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                            <span className="font-medium">{typeof b === 'string' ? b : b.nom}</span>
                            {typeof b === 'object' && b.prix > 0 && (
                              <span className="font-bold text-amber-700">{b.prix.toFixed(2)}€</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
